import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function Fizkultura() {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const goBack = useBackNavigation('/creative-direction');
  return <div className="w-full min-h-screen bg-white" data-id="element-1035">
      {/* Image Modal */}
      {selectedImage && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4" onClick={() => setSelectedImage(null)} data-id="element-1036">
          <button onClick={() => setSelectedImage(null)} className="absolute top-4 right-4 text-white hover:text-emerald-400 transition-colors" data-id="element-1037">
            <XIcon className="w-8 h-8" data-id="element-1038" />
          </button>
          <motion.img initial={{
        scale: 0.9
      }} animate={{
        scale: 1
      }} src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" data-id="element-1039" />
        </motion.div>}
      {/* Header */}
      <header className="py-8 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-1040">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-1041">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors" data-id="element-1042">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-1043" />
            <span className="font-light" data-id="element-1044">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-1045">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-1046">
        <div className="max-w-5xl mx-auto" data-id="element-1047">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-1048">
            <p className="text-emerald-600 font-light tracking-wide uppercase mb-4" data-id="element-1049">
              Creative Direction, Brand Strategy
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-1050">
              Fizkultura
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-1051">
              Studio planning and design, creative direction and brand strategy
              development for a fitness space focused on community building
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-1052">
              <p className="text-lg" data-id="element-1053">
                <span className="font-normal text-stone-900" data-id="element-1054">Role:</span>{' '}
                Creative Director, Spatial Designer
              </p>
              <p className="text-lg" data-id="element-1055">
                <span className="font-normal text-stone-900" data-id="element-1056">Scope:</span> Brand
                Identity, Spatial Planning, Visual System
              </p>
              <p className="text-lg" data-id="element-1057">
                <span className="font-normal text-stone-900" data-id="element-1058">Status:</span>{' '}
                Completed
              </p>
              <p className="text-lg" data-id="element-1059">
                <span className="font-normal text-stone-900" data-id="element-1060">Year:</span> 2024
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Overview */}
      <section className="py-20 px-6 bg-white" data-id="element-1061">
        <div className="max-w-5xl mx-auto" data-id="element-1062">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1063">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-1064">
              Project Overview
            </h2>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1065">
              Fizkultura represents a holistic approach to fitness space design,
              where community and wellness intersect. The project encompasses
              comprehensive studio planning, spatial design, and brand strategy
              development.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed mb-6" data-id="element-1066">
              Through thoughtful creative direction, the space fosters
              connection and motivation, creating an environment where members
              feel supported in their fitness journey while building lasting
              relationships.
            </p>
            <p className="text-lg text-stone-600 font-light leading-relaxed" data-id="element-1067">
              The brand identity system reflects the studio's values of
              strength, community, and accessibility—combining bold Cyrillic
              typography with a distinctive teal color palette that evokes both
              energy and calm.
            </p>
          </motion.div>
        </div>
      </section>
      {/* Brand Identity Hero */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1068">
        <div className="max-w-7xl mx-auto" data-id="element-1069">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1070">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1071">
              Brand Identity System
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1072">
              A comprehensive visual identity that balances strength with
              approachability, creating a distinctive presence in the fitness
              space.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/image%2067.png')} data-id="element-1073">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1074">
                <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/image%2067.png" alt="Fizkultura Brand Identity System" className="w-full h-auto" data-id="element-1075" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1076">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1077" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1078">
                Complete brand identity system with logo variations and
                applications
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Brand Applications */}
      <section className="py-20 px-6 bg-white" data-id="element-1079">
        <div className="max-w-7xl mx-auto" data-id="element-1080">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1081">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1082">
              Brand Applications
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1083">
              The identity system comes to life across multiple touchpoints—from
              physical spaces to merchandise and digital platforms.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1084">
              {/* Left Column */}
              <div className="space-y-8" data-id="element-1085">
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/image%2068.png')} data-id="element-1086">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1087">
                    <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/image%2068.png" alt="Brand applications - merchandise and signage" className="w-full h-auto" data-id="element-1088" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1089">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1090" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-1091">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1092">
                      Selected Design in Application
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-1093">
                      Merchandise, signage, and environmental graphics
                    </p>
                  </div>
                </motion.div>
                <motion.div initial={{
                opacity: 0,
                y: 30
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.1
              }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/%D0%BA1.jpg')} data-id="element-1094">
                  <div className="relative overflow-hidden rounded-xl shadow-lg" data-id="element-1095">
                    <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/%D0%BA1.jpg" alt="Social media promotional content" className="w-full h-auto" data-id="element-1096" />
                    <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1097">
                      <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1098" />
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-1099">
                    <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1100">
                      Social Media Promotion
                    </h4>
                    <p className="text-stone-600 font-light text-sm" data-id="element-1101">
                      Dynamic promotional content for digital campaigns
                    </p>
                  </div>
                </motion.div>
              </div>
              {/* Right Column */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/iPhone%2014%20-%201.png')} data-id="element-1102">
                <div className="relative overflow-hidden rounded-xl shadow-lg bg-stone-900" data-id="element-1103">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/iPhone%2014%20-%201.png" alt="Mobile application mockup" className="w-full h-auto" data-id="element-1104" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1105">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1106" />
                  </div>
                </div>
                <div className="mt-4" data-id="element-1107">
                  <h4 className="text-lg font-light text-stone-900 mb-2" data-id="element-1108">
                    Digital Experience
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1109">
                    For future mobile app interface with community features
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Developed Website */}
      <section className="py-20 px-6 bg-white" data-id="element-1110">
        <div className="max-w-7xl mx-auto" data-id="element-1111">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1112">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1113">
              Website Development
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1114">
              Working closely with developers, we brought the brand identity to
              life through a fully functional website. The site features class
              booking, membership management, promotional campaigns, and
              location information—all designed to create a seamless user
              experience that reflects the studio's welcoming atmosphere.
            </p>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/Group%206.png')} data-id="element-1115">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1116">
                <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/Group%206.png" alt="Fizkultura developed website" className="w-full h-auto" data-id="element-1117" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1118">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1119" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1120">
                Complete website showcasing class schedules, membership options,
                and community features
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Completed Studio Space */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1121">
        <div className="max-w-7xl mx-auto" data-id="element-1122">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1123">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1124">
              Completed Studio Space
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1125">
              The transformation from empty office space to vibrant fitness
              studio involved careful selection of furnishings, custom signage
              design, and creative repurposing of industrial lockers.
            </p>
            {/* Studio Space Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1126">
              {/* Custom Signage */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2023-06-28_12-.jpg')} data-id="element-1127">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1128">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2023-06-28_12-.jpg" alt="Custom signage and wayfinding" className="w-full h-64 object-cover" data-id="element-1129" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1130">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1131" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1132">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1133">
                    Custom Signage
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1134">
                    Motivational graphics and wayfinding
                  </p>
                </div>
              </motion.div>
              {/* Community Lounge */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0116%20(1).jpg')} data-id="element-1135">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1136">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0116%20(1).jpg" alt="Community lounge space" className="w-full h-64 object-cover" data-id="element-1137" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1138">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1139" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1140">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1141">
                    Community Lounge
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1142">
                    Social space with games and seating
                  </p>
                </div>
              </motion.div>
              {/* Changing Facilities */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0080.jpg')} data-id="element-1143">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1144">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0080.jpg" alt="Changing facilities" className="w-full h-64 object-cover" data-id="element-1145" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1146">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1147" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1148">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1149">
                    Changing Facilities
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1150">
                    Clean, functional design
                  </p>
                </div>
              </motion.div>
              {/* Custom Locker Art */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0091.jpg')} data-id="element-1151">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1152">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/DSC_0091.jpg" alt="Repurposed lockers with botanical artwork" className="w-full h-64 object-cover" data-id="element-1153" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1154">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1155" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1156">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1157">
                    Custom Locker Art
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1158">
                    Botanical illustrations on repurposed lockers
                  </p>
                </div>
              </motion.div>
              {/* Training Space */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2024-10-19_22-58-28%20(3).jpg')} data-id="element-1159">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1160">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2024-10-19_22-58-28%20(3).jpg" alt="Training space with red flooring" className="w-full h-64 object-cover" data-id="element-1161" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1162">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1163" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1164">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1165">
                    Training Area
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1166">
                    Red flooring with functional equipment
                  </p>
                </div>
              </motion.div>
              {/* Yoga Class in Session */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2024-10-19_22-58-29.jpg')} data-id="element-1167">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1168">
                  <img src="https://storage.yandexcloud.net/maria-images/images/fizkultura_ready/photo_2024-10-19_22-58-29.jpg" alt="Yoga class in session" className="w-full h-64 object-cover" data-id="element-1169" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1170">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1171" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1172">
                  <h4 className="text-base font-light text-stone-900 mb-1" data-id="element-1173">
                    Classes in Action
                  </h4>
                  <p className="text-stone-600 font-light text-sm" data-id="element-1174">
                    Yoga and stretching sessions
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Design Approach */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1175">
        <div className="max-w-5xl mx-auto" data-id="element-1176">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1177">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-1178">
              Design Approach
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-1179">
              <p data-id="element-1180">
                The design strategy centers on creating a welcoming environment
                that breaks down barriers to fitness. The bold Cyrillic
                typography establishes a strong, confident identity while the
                teal color palette adds warmth and accessibility.
              </p>
              <p data-id="element-1181">
                Spatial planning prioritizes flexible zones that accommodate
                diverse fitness activities—from high-intensity training to
                mindful movement practices. The layout encourages natural
                interaction between members while maintaining comfortable
                personal space.
              </p>
              <p data-id="element-1182">
                Every touchpoint, from signage to digital interfaces, reinforces
                the brand's commitment to community and inclusivity. The result
                is a fitness space that feels both energizing and supportive—a
                place where people don't just work out, they belong.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Branding Ideation - Moved to bottom, smaller scale */}
      <section className="py-16 px-6 bg-white border-t border-stone-200" data-id="element-1183">
        <div className="max-w-6xl mx-auto" data-id="element-1184">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1185">
            <h2 className="text-2xl font-light text-stone-900 mb-3" data-id="element-1186">
              Branding Ideation Process
            </h2>
            <p className="text-base text-stone-600 font-light mb-8 max-w-3xl" data-id="element-1187">
              The brand identity emerged through exploration of multiple visual
              directions—from geometric symbols to typographic solutions to
              Cyrillic monograms.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1188">
              {/* Triangle Concept */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://uploadthingy.s3.us-west-1.amazonaws.com/gJk81STFr6Kd4kj96qgKkF/image_63.png')} data-id="element-1189">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1190">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gJk81STFr6Kd4kj96qgKkF/image_63.png" alt="Triangle logo concept" className="w-full h-48 object-cover" data-id="element-1191" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1192">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1193" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1194">
                  <h4 className="text-sm font-light text-stone-900 mb-1" data-id="element-1195">
                    Geometric Symbol
                  </h4>
                  <p className="text-stone-600 font-light text-xs" data-id="element-1196">
                    Triangle-based exploration
                  </p>
                </div>
              </motion.div>
              {/* Typographic Concept */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://uploadthingy.s3.us-west-1.amazonaws.com/kN7rAcV3HzB5nkb4wv7zAt/image_62.png')} data-id="element-1197">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1198">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/kN7rAcV3HzB5nkb4wv7zAt/image_62.png" alt="Typographic concept" className="w-full h-48 object-cover" data-id="element-1199" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1200">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1201" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1202">
                  <h4 className="text-sm font-light text-stone-900 mb-1" data-id="element-1203">
                    Typographic Solution
                  </h4>
                  <p className="text-stone-600 font-light text-xs" data-id="element-1204">
                    Bold stacked letterforms
                  </p>
                </div>
              </motion.div>
              {/* Cyrillic Monogram */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://uploadthingy.s3.us-west-1.amazonaws.com/aqT3zkMNpFR7PFpSjvuhuA/image_61.png')} data-id="element-1205">
                <div className="relative overflow-hidden rounded-lg shadow-lg" data-id="element-1206">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/aqT3zkMNpFR7PFpSjvuhuA/image_61.png" alt="Cyrillic monogram" className="w-full h-48 object-cover" data-id="element-1207" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1208">
                    <ZoomInIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1209" />
                  </div>
                </div>
                <div className="mt-3" data-id="element-1210">
                  <h4 className="text-sm font-light text-stone-900 mb-1" data-id="element-1211">
                    Cyrillic Monogram
                  </h4>
                  <p className="text-stone-600 font-light text-xs" data-id="element-1212">
                    ФК monogram with wordmark
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}